/**
 * Standalone visual harness for SequencerPanel — used to verify the
 * three-bar fan-out UI renders correctly without booting the full
 * agent stack. Mounted at /__sequencer_demo__ in development only.
 */
import React, { useEffect, useState } from 'react';
import SequencerPanel from '@/components/draft/SequencerPanel';

const SCRIPT = [
  // t=0 — fan-out starts, all three running
  { dt: 0, op: (s) => ({
      active: true,
      agents: {
        examiner: { status: 'running', elapsed_s: 0 },
        regis:    { status: 'running', elapsed_s: 0 },
        counsel:  { status: 'running', elapsed_s: 0 },
      },
      conductor: { status: 'waiting', elapsed_s: 0 },
    })
  },
  // t=4 — counsel finishes first
  { dt: 4000, op: (s) => ({
      ...s,
      agents: { ...s.agents, counsel: { status: 'completed', elapsed_s: 4.0 } },
    })
  },
  // t=7 — regis finishes
  { dt: 3000, op: (s) => ({
      ...s,
      agents: { ...s.agents, regis: { status: 'completed', elapsed_s: 7.0 } },
    })
  },
  // t=12 — examiner finishes (longest)
  { dt: 5000, op: (s) => ({
      ...s,
      agents: { ...s.agents, examiner: { status: 'completed', elapsed_s: 12.0 } },
      conductor: { status: 'queued', elapsed_s: 0 },
    })
  },
  // t=12.5 — conductor starts
  { dt: 500, op: (s) => ({
      ...s, conductor: { status: 'running', elapsed_s: 0 },
    })
  },
  // t=15 — conductor done
  { dt: 2500, op: (s) => ({
      ...s, conductor: { status: 'completed', elapsed_s: 2.5 },
    })
  },
];

export default function SequencerDemoPage() {
  const [state, setState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let cur = null;
    const run = async () => {
      for (const step of SCRIPT) {
        if (step.dt > 0) await new Promise((r) => setTimeout(r, step.dt));
        if (cancelled) return;
        cur = step.op(cur);
        setState(cur);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      style={{
        background: '#FAF7F2',
        minHeight: '100vh',
        padding: '40px',
        fontFamily: 'system-ui, sans-serif',
      }}
      data-testid="sequencer-demo-page"
    >
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <h1 style={{ fontSize: 18, color: '#1F1F1F', marginBottom: 8 }}>Sequencer demo</h1>
        <p style={{ fontSize: 12, color: '#666', marginBottom: 24 }}>
          Synthetic event timeline replaying the SSE state machine for visual verification.
        </p>
        <SequencerPanel state={state} isArabic={false} />
      </div>
    </div>
  );
}
